import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '../../design-system/components/WebAppShell';
import { reviewDesk } from '../../design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import { useTabAccess } from '../../hooks/useTabAccess';
import { cohortsAPI, Cohort } from '../../services/api';
import MentorTrainingAPI from '../../services/api/mentorTrainingAPI';
import PublishDrillModal from './PublishDrillModal';
import CohortSettingsModal from './CohortSettingsModal';
import {
  DrillSummary,
  TARGET_LEVELS,
  DrillTargetLevel,
} from '../../types/mentorTraining';

type RoleView = 'student' | 'instructor';

function levelLabel(target: DrillTargetLevel, t: (k: string, fb?: string) => string): string {
  const map: Record<DrillTargetLevel, string> = {
    acc: 'mentorTraining.level.acc',
    pcc: 'mentorTraining.level.pcc',
    mcc: 'mentorTraining.level.mcc',
  };
  return t(map[target] ?? 'mentorTraining.level.unknown');
}

function StatusChip({ status, t }: { status: string; t: (k: string, fb?: string) => string }) {
  let label = '';
  let bg: string = reviewDesk.cardMuted;
  let color: string = reviewDesk.inkSoft;
  if (status === 'submitted') {
    label = t('mentorTraining.status.submitted');
    bg = reviewDesk.successSoft;
    color = reviewDesk.success;
  } else if (status === 'in_progress') {
    label = t('mentorTraining.status.inProgress');
    bg = reviewDesk.amberSoft;
    color = reviewDesk.amber;
  } else {
    label = t('mentorTraining.status.notStarted');
  }
  return (
    <View style={[styles.chip, { backgroundColor: bg }]}>
      <Text style={[styles.chipText, { color }]}>{label}</Text>
    </View>
  );
}

function DrillCard({
  drill,
  onPress,
  showAttemptStatus,
  showSubmissionCount,
  t,
}: {
  drill: DrillSummary;
  onPress: () => void;
  showAttemptStatus?: boolean;
  showSubmissionCount?: boolean;
  t: (k: string, fb?: string) => string;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => [
        styles.drillCard,
        hovered && { borderColor: reviewDesk.lineStrong },
        pressed && { opacity: 0.92 },
      ]}
    >
      <View style={styles.drillCardHeader}>
        <View style={styles.drillCardLevelPill}>
          <Text style={styles.drillCardLevelText}>{levelLabel(drill.target_level, t)}</Text>
        </View>
        {showAttemptStatus && drill.my_attempt_status && (
          <StatusChip status={drill.my_attempt_status} t={t} />
        )}
      </View>
      <Text style={styles.drillCardTitle} numberOfLines={2}>
        {drill.title}
      </Text>
      {!!drill.module_name && (
        <Text style={styles.drillCardModule} numberOfLines={1}>
          {drill.module_name}
        </Text>
      )}
      <View style={styles.drillCardFooter}>
        <View style={styles.drillCardMetaRow}>
          <MaterialIcons name="schedule" size={13} color={reviewDesk.muted} />
          <Text style={styles.drillCardMeta}>
            {drill.published_at
              ? new Date(drill.published_at).toLocaleDateString()
              : new Date(drill.created_at).toLocaleDateString()}
          </Text>
        </View>
        {showSubmissionCount && (
          <View style={styles.drillCardMetaRow}>
            <MaterialIcons name="group" size={13} color={reviewDesk.muted} />
            <Text style={styles.drillCardMeta}>
              {t('mentorTraining.submissions')}: {drill.submitted_count}/{drill.attempt_count || 0}
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  );
}

function EmptyState({ kind, t }: { kind: 'student-empty' | 'instructor-empty' | 'no-cohorts'; t: (k: string, fb?: string) => string }) {
  const messages: Record<typeof kind, [string, string]> = {
    'student-empty': ['mentorTraining.empty.studentTitle', 'mentorTraining.empty.studentBody'],
    'instructor-empty': ['mentorTraining.empty.instructorTitle', 'mentorTraining.empty.instructorBody'],
    'no-cohorts': ['mentorTraining.empty.noCohortsTitle', 'mentorTraining.empty.noCohortsBody'],
  };
  const [titleKey, bodyKey] = messages[kind];
  return (
    <View style={styles.emptyState}>
      <MaterialIcons name="school" size={36} color={reviewDesk.muted} />
      <Text style={styles.emptyTitle}>{t(titleKey)}</Text>
      <Text style={styles.emptyBody}>{t(bodyKey)}</Text>
    </View>
  );
}

export default function MentorTrainingHomeScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { isMentorOrAdmin, isB2BUser, user } = useTabAccess();
  const [view, setView] = useState<RoleView>(isMentorOrAdmin ? 'instructor' : 'student');
  const [drills, setDrills] = useState<DrillSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [selectedCohortId, setSelectedCohortId] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [settingsCohort, setSettingsCohort] = useState<Cohort | null>(null);

  const schoolId = (user as any)?.school_id ?? null;
  const selectedCohort = useMemo(
    () => cohorts.find((c) => c.id === selectedCohortId) || null,
    [cohorts, selectedCohortId]
  );
  const selectedHasFeature = !!selectedCohort?.enabled_features?.includes('mentor_training');
  const featureEnabledCohorts = useMemo(
    () => cohorts.filter((c) => c.enabled_features?.includes('mentor_training')),
    [cohorts]
  );

  const loadStudentDrills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await MentorTrainingAPI.listMyDrills();
      setDrills(data);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInstructorCohorts = useCallback(async () => {
    if (!schoolId) {
      setCohorts([]);
      return;
    }
    try {
      const list = await cohortsAPI.list(schoolId);
      const active = list.filter((c) => !c.archived_at);
      setCohorts(active);
      if (active.length > 0 && selectedCohortId === null) {
        setSelectedCohortId(active[0].id);
      }
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }, [schoolId, selectedCohortId]);

  const loadInstructorDrills = useCallback(
    async (cohortId: number) => {
      setLoading(true);
      setError(null);
      try {
        const data = await MentorTrainingAPI.listCohortDrills(cohortId);
        setDrills(data);
      } catch (e: any) {
        setError(e?.message || String(e));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (view === 'student') {
      loadStudentDrills();
    } else {
      loadInstructorCohorts();
    }
  }, [view, loadStudentDrills, loadInstructorCohorts]);

  useEffect(() => {
    if (view !== 'instructor' || selectedCohortId === null) return;
    if (!selectedHasFeature) {
      // Skip the drill fetch when the cohort hasn't opted in — backend
      // would reject anyway and the empty-state CTA handles UX.
      setDrills([]);
      setLoading(false);
      return;
    }
    loadInstructorDrills(selectedCohortId);
  }, [view, selectedCohortId, selectedHasFeature, loadInstructorDrills]);

  const onDrillPress = useCallback(
    (drill: DrillSummary) => {
      if (view === 'instructor') {
        router.push(`/mentor-training/instructor/${drill.id}` as any);
      } else {
        router.push(`/mentor-training/drill/${drill.id}` as any);
      }
    },
    [router, view]
  );

  const headerActions = useMemo(() => {
    if (!isMentorOrAdmin) return null;
    return (
      <View style={styles.viewToggleRow}>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => setView('student')}
          style={[styles.viewToggleBtn, view === 'student' && styles.viewToggleBtnActive]}
        >
          <Text style={[styles.viewToggleText, view === 'student' && styles.viewToggleTextActive]}>
            {t('mentorTraining.view.student')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => setView('instructor')}
          style={[styles.viewToggleBtn, view === 'instructor' && styles.viewToggleBtnActive]}
        >
          <Text style={[styles.viewToggleText, view === 'instructor' && styles.viewToggleTextActive]}>
            {t('mentorTraining.view.instructor')}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }, [isMentorOrAdmin, view, t]);

  return (
    <WebAppShell active="mentor-training" topRight={headerActions}>
      <View style={styles.container}>
        <View style={styles.heroBlock}>
          <View style={styles.heroBadge}>
            <Text style={styles.heroBadgeText}>MCQ</Text>
          </View>
          <Text style={styles.heroTitle}>
            {t('mentorTraining.title')}
          </Text>
          <Text style={styles.heroBody}>
            {t(
              'mentorTraining.subtitle'
            )}
          </Text>
        </View>

        {view === 'instructor' && (
          <View style={styles.instructorBar}>
            <View style={styles.cohortRow}>
              <Text style={styles.cohortRowLabel}>
                {t('mentorTraining.cohort')}:
              </Text>
              {cohorts.length > 0 ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.cohortScroller}>
                  {cohorts.map((c) => {
                    const active = c.id === selectedCohortId;
                    const enabled = c.enabled_features?.includes('mentor_training');
                    return (
                      <TouchableOpacity
                        key={c.id}
                        onPress={() => setSelectedCohortId(c.id)}
                        style={[styles.cohortPill, active && styles.cohortPillActive]}
                      >
                        {!enabled && (
                          <MaterialIcons
                            name="lock-outline"
                            size={12}
                            color={active ? reviewDesk.accent : reviewDesk.muted}
                          />
                        )}
                        <Text style={[styles.cohortPillText, active && styles.cohortPillTextActive]}>
                          {c.name}
                        </Text>
                        <Text style={[styles.cohortPillCount, active && styles.cohortPillCountActive]}>
                          {c.member_count}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : (
                <Text style={styles.cohortRowEmpty}>
                  {t('mentorTraining.empty.noCohortsTitle')}
                </Text>
              )}
            </View>
            <View style={styles.instructorActions}>
              {selectedCohort && (
                <TouchableOpacity
                  onPress={() => setSettingsCohort(selectedCohort)}
                  style={styles.settingsBtn}
                  accessibilityRole="button"
                >
                  <MaterialIcons name="settings" size={14} color={reviewDesk.inkSoft} />
                  <Text style={styles.settingsBtnText}>
                    {t('mentorTraining.cohortSettings')}
                  </Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                onPress={() => setPublishOpen(true)}
                style={[
                  styles.publishCta,
                  (cohorts.length === 0 || !selectedHasFeature) && { opacity: 0.5 },
                ]}
                disabled={cohorts.length === 0 || !selectedHasFeature}
                accessibilityRole="button"
              >
                <MaterialIcons name="add-circle-outline" size={16} color="#fff" />
                <Text style={styles.publishCtaText}>
                  {t('mentorTraining.newDrill')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {view === 'instructor' && selectedCohort && !selectedHasFeature && cohorts.length > 0 && (
          <View style={styles.featureOffBox}>
            <View style={styles.featureOffIcon}>
              <MaterialIcons name="lock-outline" size={20} color={reviewDesk.amber} />
            </View>
            <View style={{ flex: 1, gap: 6 }}>
              <Text style={styles.featureOffTitle}>
                {t(
                  'mentorTraining.featureOffTitle'
                )}
              </Text>
              <Text style={styles.featureOffBody}>
                {t(
                  'mentorTraining.featureOffBody'
                )}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => setSettingsCohort(selectedCohort)}
              style={styles.featureOffCta}
            >
              <Text style={styles.featureOffCtaText}>
                {t('mentorTraining.enableFeature')}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {loading && (
          <View style={styles.loaderBox}>
            <ActivityIndicator size="small" color={reviewDesk.accent} />
          </View>
        )}

        {!loading && error && (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {!loading && !error && drills.length === 0 && (
          <EmptyState
            kind={
              view === 'instructor'
                ? cohorts.length === 0
                  ? 'no-cohorts'
                  : 'instructor-empty'
                : 'student-empty'
            }
            t={t}
          />
        )}

        {!loading && drills.length > 0 && (
          <View style={styles.drillGrid}>
            {drills.map((drill) => (
              <DrillCard
                key={drill.id}
                drill={drill}
                onPress={() => onDrillPress(drill)}
                showAttemptStatus={view === 'student'}
                showSubmissionCount={view === 'instructor'}
                t={t}
              />
            ))}
          </View>
        )}

        <View style={styles.footnote}>
          <MaterialIcons name="info-outline" size={14} color={reviewDesk.muted} />
          <Text style={styles.footnoteText}>
            {t(
              'mentorTraining.footnote'
            )}
          </Text>
        </View>
      </View>

      <PublishDrillModal
        visible={publishOpen}
        cohorts={featureEnabledCohorts}
        defaultCohortId={selectedCohortId}
        onClose={() => setPublishOpen(false)}
        onCreated={(drill) => {
          if (drill.cohort_id === selectedCohortId) {
            setDrills((prev) => [drill, ...prev]);
          } else {
            setSelectedCohortId(drill.cohort_id);
          }
        }}
      />

      <CohortSettingsModal
        visible={settingsCohort !== null}
        cohort={settingsCohort}
        schoolId={schoolId}
        onClose={() => setSettingsCohort(null)}
        onSaved={(updated) => {
          setCohorts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
          setSettingsCohort(null);
          if (selectedCohortId === updated.id) {
            // Re-fetch drills now that the feature flag may have flipped on/off.
            if (updated.enabled_features?.includes('mentor_training')) {
              loadInstructorDrills(updated.id);
            } else {
              setDrills([]);
            }
          }
        }}
      />
    </WebAppShell>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 28,
    paddingVertical: 24,
    gap: 22,
  },
  heroBlock: {
    backgroundColor: reviewDesk.accentWash,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 14,
    padding: 22,
    gap: 10,
  },
  heroBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 6,
    backgroundColor: reviewDesk.accent,
  },
  heroBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
  },
  heroTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: reviewDesk.ink,
  },
  heroBody: {
    fontSize: 14,
    lineHeight: 21,
    color: reviewDesk.inkSoft,
    maxWidth: 720,
  },
  viewToggleRow: {
    flexDirection: 'row',
    backgroundColor: reviewDesk.cardMuted,
    borderRadius: 8,
    padding: 4,
    gap: 4,
  },
  viewToggleBtn: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 6,
  },
  viewToggleBtnActive: {
    backgroundColor: reviewDesk.card,
    shadowColor: reviewDesk.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.4,
    shadowRadius: 3,
    elevation: 1,
  },
  viewToggleText: {
    fontSize: 13,
    color: reviewDesk.inkSoft,
    fontWeight: '500',
  },
  viewToggleTextActive: {
    color: reviewDesk.ink,
    fontWeight: '600',
  },
  instructorBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  cohortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  cohortRowLabel: {
    fontSize: 13,
    color: reviewDesk.muted,
    fontWeight: '500',
  },
  cohortRowEmpty: {
    fontSize: 13,
    color: reviewDesk.muted,
    fontStyle: 'italic',
  },
  instructorActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  settingsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 8,
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 8,
  },
  settingsBtnText: { fontSize: 12, color: reviewDesk.inkSoft, fontWeight: '500' },
  publishCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 9,
    backgroundColor: reviewDesk.accent,
    borderRadius: 8,
  },
  publishCtaText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  featureOffBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    padding: 16,
    backgroundColor: reviewDesk.amberSoft,
    borderColor: reviewDesk.amber,
    borderWidth: 1,
    borderRadius: 10,
  },
  featureOffIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureOffTitle: { fontSize: 14, fontWeight: '700', color: reviewDesk.ink },
  featureOffBody: { fontSize: 13, color: reviewDesk.inkSoft, lineHeight: 19 },
  featureOffCta: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    backgroundColor: reviewDesk.amber,
    borderRadius: 8,
  },
  featureOffCtaText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  cohortScroller: {
    flexGrow: 0,
  },
  cohortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: reviewDesk.card,
    borderRadius: 20,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    marginRight: 8,
  },
  cohortPillActive: {
    borderColor: reviewDesk.accent,
    backgroundColor: reviewDesk.accentSoft,
  },
  cohortPillText: {
    fontSize: 13,
    color: reviewDesk.inkSoft,
    fontWeight: '500',
  },
  cohortPillTextActive: {
    color: reviewDesk.accent,
    fontWeight: '600',
  },
  cohortPillCount: {
    fontSize: 11,
    color: reviewDesk.muted,
    paddingHorizontal: 6,
    paddingVertical: 1,
    backgroundColor: reviewDesk.cardMuted,
    borderRadius: 8,
  },
  cohortPillCountActive: {
    color: reviewDesk.accent,
    backgroundColor: '#fff',
  },
  loaderBox: {
    paddingVertical: 30,
    alignItems: 'center',
  },
  errorBox: {
    backgroundColor: '#FFE7E1',
    borderColor: '#F1A89C',
    borderWidth: 1,
    padding: 14,
    borderRadius: 10,
  },
  errorText: {
    color: '#7A2B1F',
    fontSize: 13,
  },
  emptyState: {
    alignItems: 'center',
    gap: 8,
    padding: 40,
    backgroundColor: reviewDesk.cardMuted,
    borderRadius: 12,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderStyle: 'dashed',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: reviewDesk.ink,
    marginTop: 4,
  },
  emptyBody: {
    fontSize: 13,
    color: reviewDesk.inkSoft,
    textAlign: 'center',
    maxWidth: 480,
    lineHeight: 19,
  },
  drillGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  drillCard: {
    width: '100%',
    maxWidth: 320,
    minHeight: 150,
    padding: 16,
    backgroundColor: reviewDesk.card,
    borderColor: reviewDesk.line,
    borderWidth: 1,
    borderRadius: 12,
    gap: 8,
  },
  drillCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  drillCardLevelPill: {
    paddingHorizontal: 9,
    paddingVertical: 3,
    backgroundColor: reviewDesk.accentSoft,
    borderRadius: 5,
  },
  drillCardLevelText: {
    fontSize: 11,
    fontWeight: '700',
    color: reviewDesk.accent,
    letterSpacing: 0.5,
  },
  drillCardTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: reviewDesk.ink,
    lineHeight: 21,
  },
  drillCardModule: {
    fontSize: 12,
    color: reviewDesk.muted,
  },
  drillCardFooter: {
    marginTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  drillCardMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  drillCardMeta: {
    fontSize: 12,
    color: reviewDesk.muted,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 5,
  },
  chipText: {
    fontSize: 11,
    fontWeight: '600',
  },
  footnote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
  },
  footnoteText: {
    fontSize: 11,
    color: reviewDesk.muted,
    fontStyle: 'italic',
  },
});
